import 'pixi.js/math-extras'; // eslint-disable-line
import * as PIXI from 'pixi.js';
import gsap from 'gsap';
import { CustomWiggle } from 'gsap/CustomWiggle';

import * as animationsManager from './animationsManager';

import OpponentContainer from '../containers/oponentContainer';
import PlayerContainer from '../containers/playerContainer';
import ActionButtonsContainer from '../containers/actionButtonsContainer';
import { APP_HEIGHT, APP_WIDTH, CARD_HEIGHT, CARD_WIDTH, MIDDLE_X_LOCATION, RESOLUTION, redrawFullScreenOverlay } from '../utils/commonUtils';
import { getGlobalEventEmitter } from '../utils/globalEventEmitter';
import AssetsLoader from '../utils/assetsLoader';
import ButtonContainer from '../containers/buttonContainer';
import MulliganContainer from '../containers/mulliganContainer';
import CardInfoContainer from '../containers/cardInfoContainer';
import WinLoseContainer from '../containers/winLoseContainer';
import TrashOverlayContainer from '../containers/trashOverlayContainer';
import WavesShader from '../shaders/WavesShader';
import PlayerTriggerContainer from '../containers/playerTriggerContainer';
import FtuePopupContainer from '../containers/ftuePopupContainer';
import CardContainer, { CardVisualsContainer } from '../containers/cardContainer';
import CardHighlightFilter from '../filters/CardHighlightFilter';
import SearchDeckContainer from '../containers/searchDeckContainer';
import OpponentSearchDeckContainer from '../containers/opponentSearchDeckContainer';
import SelectFromTrashContainer from '../containers/selectFromTrashContainer';
import ChooseFromOpponentHandContainer from '../containers/chooseFromOpponentHandContainer';
import RevealCardsContainer from '../containers/revealCardsContainer';
import SettingsOverlayContainer from '../containers/settingsOverlayContainer';

const PLAYER_FIELD_LABEL = 'PlayerField';
const OPPONENT_FIELD_LABEL = 'OppentField';

function createPlane(shader, { flipVertically, yOffset } = { flipVertically: false, yOffset: 260 }) {
    const totalSections = 10;
    const vertices = [];
    const uvs = [];
    for (let i = 0; i <= totalSections; i++) {
        const curveHeight = Math.sin((i / totalSections) * Math.PI) * 50 * (flipVertically ? -1 : 1);
        const lowerYOffset = 800;
        vertices.push((i / totalSections) * APP_WIDTH, 0 - curveHeight + yOffset + lowerYOffset);
        uvs.push(i / totalSections, flipVertically ? 1 : 0);
        vertices.push((i / totalSections) * APP_WIDTH, APP_WIDTH - curveHeight + yOffset);
        uvs.push(i / totalSections, flipVertically ? 0 : 1);
    }

    const indices = [];
    for (let i = 0; i < totalSections; i++) {
        indices.push(i * 2, (i+1) * 2, (i+1) * 2 + 1);
        indices.push(i * 2, (i+1) * 2 + 1, i * 2 + 1);
    }

    const plane = new PIXI.MeshSimple({
        vertices: new Float32Array(vertices),
        uvs: new Float32Array(uvs),
        indices: new Uint32Array(indices),
        autoUpdate: true,
        shader,
    });

    return plane;
}

export default class SceneManager {
    constructor() {
        this.scene = new PIXI.Container();
        this.scene.label = 'game';
        this.screenWidth = APP_WIDTH;
        this.screenHeight = APP_HEIGHT;
    }

    resize(width, height) {
        this.screenWidth = width;
        this.screenHeight = height;
        this.applyBoardAnchor();
        this.anchorScreenEdges();
        this.resizeOverlays(width, height);
    }

    anchorScreenEdges() {
        const offsetX = (this.screenWidth - APP_WIDTH) / 2;
        const sideButtonX = APP_WIDTH - 100 + offsetX;

        for (const label of [
            'sideButtonHighlight',
            'endTurnButton',
            'cancelButton',
            'enemyTurnButton',
            'enemyBlockerButton',
            'enemyCounterButton',
            'passButton',
        ]) {
            const button = this.scene.getChildByLabel(label);
            if (button) button.x = sideButtonX;
        }

        const currentPhaseBg = this.scene.getChildByLabel('currentPhaseBg');
        if (currentPhaseBg) currentPhaseBg.x = -offsetX;

        const currentPhaseText = this.scene.getChildByLabel('currentPhaseText');
        if (currentPhaseText) currentPhaseText.x = -offsetX + 20;
    }

    resizeOverlays(width, height) {
        const overlayLabels = [
            'playerTriggerContainer',
            'searchDeckContainer',
            'chooseFromOpponentHandContainer',
            'revealCardsContainer',
            'selectFromTrashContainer',
            'mulliganContainer',
            'cardInfoContainer',
            'winLoseContainer',
            'settingsOverlayContainer',
        ];

        for (const label of overlayLabels) {
            this.scene.getChildByLabel(label)?.resize?.(width, height);
        }

        const effectActivationOverlayGraphics = this.scene.getChildByLabel('effectActivationOverlayGraphics');
        if (effectActivationOverlayGraphics) {
            redrawFullScreenOverlay(effectActivationOverlayGraphics, width, height);
        }

        const fadeInOutLayout = this.scene.getChildByLabel('fadeInOutLayout');
        if (fadeInOutLayout) {
            const offsetX = (width - APP_WIDTH) / 2;
            fadeInOutLayout.clear();
            fadeInOutLayout.rect(-offsetX, 0, width, height);
            fadeInOutLayout.fill({ color: '#000000' });
        }
    }

    applyBoardAnchor() {
        const offsetX = (this.screenWidth - APP_WIDTH) / 2;
        this.scene.x = offsetX;
        if (this.addedStageClickListener) {
            this.scene.hitArea = new PIXI.Rectangle(-offsetX, 0, this.screenWidth, this.screenHeight);
        }
    }

    cleanup() {
        PIXI.Ticker.shared.remove(this.sideButtonHighlightTicker);
    }

    updateScene(gameState) {
        if (!this.firstUpdate) {
            this.firstUpdate = true;
        }

        console.log(gameState);

        this.unhighlightAllCards();
        this.hideSkulls();

        const isMulliganStep = gameState.pendingAction?.type === 'MULLIGAN_PROMPT';

        if (!this.waterBgAdded) {
            this.waterBgAdded = true;

            const wavesShader = new WavesShader();
            this.wavesTimer = 0;
            PIXI.Ticker.shared.add((ticker) => {
                this.wavesTimer = this.wavesTimer + ticker.deltaTime * 0.008;
                wavesShader.resources.timeUniforms.uniforms._time = this.wavesTimer;
            });

            const opponentWaves = createPlane(wavesShader);
            opponentWaves.label = 'opponentWaves';
            opponentWaves.visible = !gameState.isMyTurn;
            this.scene.addChild(opponentWaves);

            const playerWaves = createPlane(wavesShader, { flipVertically: true, yOffset: 140 });
            playerWaves.label = 'playerWaves';
            playerWaves.visible = gameState.isMyTurn;
            this.scene.addChild(playerWaves);
        } else {
            const opponentWaves = this.scene.getChildByLabel('opponentWaves');
            opponentWaves.visible = !gameState.isMyTurn;
            const playerWaves = this.scene.getChildByLabel('playerWaves');
            playerWaves.visible = gameState.isMyTurn;
        }

        if (!this.playerAndOpponentContainersAdded) {
            this.playerAndOpponentContainersAdded = true;

            const playerContainer = new PlayerContainer(gameState.player);
            playerContainer.label = PLAYER_FIELD_LABEL;
            playerContainer.x = MIDDLE_X_LOCATION;
            playerContainer.y = APP_HEIGHT;

            const opponentContainer = new OpponentContainer(gameState.opponent);
            opponentContainer.x = MIDDLE_X_LOCATION;
            opponentContainer.y = 0;
            opponentContainer.label = OPPONENT_FIELD_LABEL;

            this.scene.addChild(playerContainer);
            this.scene.addChild(opponentContainer);
        } else {
            this.scene.getChildByLabel(PLAYER_FIELD_LABEL).updateLeader(gameState.player, gameState.clientGameState);
            this.scene.getChildByLabel(OPPONENT_FIELD_LABEL).updateLeader(gameState.opponent, gameState.clientGameState);
            this.scene.getChildByLabel(PLAYER_FIELD_LABEL).updateHand(gameState, gameState.player, gameState.clientGameState);
            this.scene.getChildByLabel(OPPONENT_FIELD_LABEL).updateHand(gameState, gameState.opponent, gameState.clientGameState);
            this.scene.getChildByLabel(PLAYER_FIELD_LABEL).updateMainDeck(gameState.player.deckCount);
            this.scene.getChildByLabel(OPPONENT_FIELD_LABEL).updateMainDeck(gameState.opponent.deckCount);
            this.scene.getChildByLabel(PLAYER_FIELD_LABEL).updateCharacterArea(gameState.player.field_characters, gameState.clientGameState);
            this.scene.getChildByLabel(OPPONENT_FIELD_LABEL).updateCharacterArea(gameState.opponent.field_characters, gameState.clientGameState);
            this.scene.getChildByLabel(PLAYER_FIELD_LABEL).updateDonsContainer(gameState.player, gameState.clientGameState);
            this.scene.getChildByLabel(OPPONENT_FIELD_LABEL).updateDonsContainer(gameState.opponent, gameState.clientGameState);
            this.scene.getChildByLabel(PLAYER_FIELD_LABEL).updateStageContainer(gameState.player, gameState.clientGameState);
            this.scene.getChildByLabel(OPPONENT_FIELD_LABEL).updateStageContainer(gameState.opponent, gameState.clientGameState);
            this.scene.getChildByLabel(PLAYER_FIELD_LABEL).updateTrashContainer(gameState.player);
            this.scene.getChildByLabel(OPPONENT_FIELD_LABEL).updateTrashContainer(gameState.opponent);
        }

        if (!this.sideButtonsAdded) {
            this.sideButtonsAdded = true;

            const sideButtonHighlightFilter = new CardHighlightFilter();
            sideButtonHighlightFilter.setHighlightParameters(3, 3, '#0084ff');

            this.sideButtonHighlightTimer = 0;
            this.sideButtonHighlightTicker = (ticker) => {
                this.sideButtonHighlightTimer = this.sideButtonHighlightTimer + ticker.deltaTime * 0.016;
                sideButtonHighlightFilter.resources.timeUniforms.uniforms.uTime = this.sideButtonHighlightTimer;
            }
            PIXI.Ticker.shared.add(this.sideButtonHighlightTicker);

            const sideButtonHighlight = new PIXI.Sprite(PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.SIDE_BUTTON_HIGHLIGHT_MASK));
            sideButtonHighlight.label = 'sideButtonHighlight';
            sideButtonHighlight.x = APP_WIDTH - 100;
            sideButtonHighlight.y = APP_HEIGHT / 2 - 7;
            sideButtonHighlight.anchor.set(0.5);
            sideButtonHighlight.scale.set(1.3);
            sideButtonHighlight.visible = false;
            sideButtonHighlight.filters = [sideButtonHighlightFilter];
            this.scene.addChild(sideButtonHighlight);

            const endTurnButton = new ButtonContainer({ bgAssetName: AssetsLoader.eDefaultAssetsAliases.END_TURN_BUTTON, eventName: 'endTurnClicked' });
            endTurnButton.label = 'endTurnButton';
            endTurnButton.x = APP_WIDTH - 100;
            endTurnButton.y = APP_HEIGHT / 2;
            endTurnButton.visible = gameState.isMyTurn;
            this.scene.addChild(endTurnButton);

            const cancelButton = new ButtonContainer({ bgAssetName: AssetsLoader.eDefaultAssetsAliases.CANCEL_BUTTON, eventName: 'cancelClicked' });
            cancelButton.label = 'cancelButton';
            cancelButton.x = APP_WIDTH - 100;
            cancelButton.y = APP_HEIGHT / 2;
            cancelButton.visible = false;
            this.scene.addChild(cancelButton);

            const enemyTurnButton = new ButtonContainer({ bgAssetName: AssetsLoader.eDefaultAssetsAliases.ENEMY_TURN_BUTTON });
            enemyTurnButton.label = 'enemyTurnButton';
            enemyTurnButton.x = APP_WIDTH - 100;
            enemyTurnButton.y = APP_HEIGHT / 2;
            enemyTurnButton.visible = !gameState.isMyTurn;
            this.scene.addChild(enemyTurnButton);

            const enemyBlockerButton = new ButtonContainer({ bgAssetName: AssetsLoader.eDefaultAssetsAliases.ENEMY_BLOCKER_BUTTON });
            enemyBlockerButton.label = 'enemyBlockerButton';
            enemyBlockerButton.x = APP_WIDTH - 100;
            enemyBlockerButton.y = APP_HEIGHT / 2;
            enemyBlockerButton.visible = false;
            this.scene.addChild(enemyBlockerButton);

            const enemyCounterButton = new ButtonContainer({ bgAssetName: AssetsLoader.eDefaultAssetsAliases.ENEMY_COUNTER_BUTTON });
            enemyCounterButton.label = 'enemyCounterButton';
            enemyCounterButton.x = APP_WIDTH - 100;
            enemyCounterButton.y = APP_HEIGHT / 2;
            enemyCounterButton.visible = false;
            this.scene.addChild(enemyCounterButton);

            const passButton = new ButtonContainer({ bgAssetName: AssetsLoader.eDefaultAssetsAliases.PASS_BUTTON, eventName: 'passClicked' });
            passButton.label = 'passButton';
            passButton.x = APP_WIDTH - 100;
            passButton.y = APP_HEIGHT / 2;
            passButton.visible = false;
            this.scene.addChild(passButton);
        } else {
            const sideButtonHighlight = this.scene.getChildByLabel('sideButtonHighlight');
            const endTurnButton = this.scene.getChildByLabel('endTurnButton');
            const cancelButton = this.scene.getChildByLabel('cancelButton');
            const enemyTurnButton = this.scene.getChildByLabel('enemyTurnButton');
            const passButton = this.scene.getChildByLabel('passButton');
            const enemyCounterButton = this.scene.getChildByLabel('enemyCounterButton');
            const enemyBlockerButton = this.scene.getChildByLabel('enemyBlockerButton');

            sideButtonHighlight.visible = false;
            endTurnButton.visible = false;
            cancelButton.visible = false;
            enemyTurnButton.visible = false;
            passButton.visible = false;
            enemyCounterButton.visible = false;
            enemyBlockerButton.visible = false;

            if (gameState.isMyTurn) {
                if (gameState.clientGameState.choosingAttackTargetMode) {
                    cancelButton.visible = true;
                } else if (gameState.clientGameState.choosingEffectTargetMode || gameState.clientGameState.choosingReturnDonsMode) {
                    passButton.visible = true;

                    if (gameState.clientGameState.choosingEffectTargetMode && gameState.clientGameState.selectableCards.length === 0) {
                        sideButtonHighlight.visible = true;
                    }
                } else if (gameState.clientGameState.enemyCounterMode) {
                    enemyCounterButton.visible = true;
                } else if (gameState.clientGameState.enemyBlockerMode) {
                    enemyBlockerButton.visible = true;
                } else if (gameState.clientGameState.promptMode) {
                    passButton.visible = true;
                } else {
                    endTurnButton.visible = true;
                    if (gameState.clientGameState.noMoreMoves) {
                        sideButtonHighlight.visible = true;
                    }
                }
            } else {
                if (gameState.clientGameState.choosingBlockerMode || gameState.clientGameState.choosingCounterMode || gameState.clientGameState.choosingEffectTargetMode  || gameState.clientGameState.choosingReturnDonsMode) {
                    passButton.visible = true;
                } else {
                    enemyTurnButton.visible = true;
                }
            }
        }

        if (!this.currentPhaseVisualsAdded) {
            this.currentPhaseVisualsAdded = true;

            const gradient1 = new PIXI.FillGradient({
                type: 'linear',
                start: { x: 0, y: 0.5 },
                end: { x: 1, y: 0.5 },
                colorStops: [
                    { offset: 0, color: '#000000' },
                    { offset: 0.6, color: '#000000' },
                    { offset: 1, color: 'rgba(0, 0, 0, 0)' },
                ],
            });

            const currentPhaseBg = new PIXI.Graphics();
            currentPhaseBg.label = 'currentPhaseBg';
            currentPhaseBg.visible = true;
            currentPhaseBg.x = 0;
            currentPhaseBg.y = APP_HEIGHT / 2 + 40;
            currentPhaseBg.rect(0, -30, 300, 60);
            currentPhaseBg.fill(gradient1);
            currentPhaseBg.alpha = 0.7;
            this.scene.addChild(currentPhaseBg);

            const currentPhaseText = new PIXI.Text({
                text: '',
                style: {
                    fontFamily: 'Arial',
                    fontSize: 35,
                    fill: '#ffffff',
                },
            });
            currentPhaseText.label = 'currentPhaseText';
            currentPhaseText.anchor.set(0, 0.5);
            currentPhaseText.visible = true;
            currentPhaseText.x = 20;
            currentPhaseText.y = APP_HEIGHT / 2 + 40;
            this.scene.addChild(currentPhaseText);
        } else {
            const currentPhaseText = this.scene.getChildByLabel('currentPhaseText');

            if (gameState.pendingAction?.type === 'BLOCKER_STEP') {
                currentPhaseText.text = 'Blocker Step';
            } else if (gameState.pendingAction?.type === 'COUNTER_STEP') {
                currentPhaseText.text = 'Counter Step';
            } else {
                currentPhaseText.text = `${gameState.currentPhase} Phase`;
            }
        }

        if (!this.attackVisualsAdded) {
            this.attackVisualsAdded = true;

            const attackArrowTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.ATTACK_ARROW);
            const attackArrow = new PIXI.Sprite(attackArrowTexture);
            attackArrow.label = 'attackArrow';
            attackArrow.anchor.set(0, 0.5);
            attackArrow.visible = false;
            attackArrow.eventMode = 'none';
            this.scene.addChild(attackArrow);

            let time = 0;
            PIXI.Ticker.shared.add((ticker) => {
                time += ticker.deltaTime;
                attackArrow.alpha = Math.sin(time * 0.05) * 0.5 + 0.5;
            });
        } else {
            const attackArrow = this.scene.getChildByLabel('attackArrow');
            attackArrow.visible = false;

            if (gameState.pendingAction?.type === 'BLOCKER_STEP' || gameState.pendingAction?.type === 'COUNTER_STEP') {
                let defenderId = gameState.pendingAction.defenderId || gameState.pendingAction.originalDefenderId;
                if (gameState.pendingAction.type === 'BLOCKER_STEP' && gameState.clientGameState.selectedCard) {
                    defenderId = gameState.clientGameState.selectedCard;
                }

                const attackerCardContainer = this.findCardContainer(gameState.pendingAction.attackerId);
                const defenderCardContainer = this.findCardContainer(defenderId);
                const attackerPosition = attackerCardContainer.getGlobalPosition();
                const defenderPosition = defenderCardContainer.getGlobalPosition();

                if (attackerCardContainer.cardInstance.ownerPlayerId === gameState.player.playerId) {
                    attackerPosition.y -= CARD_HEIGHT * attackerCardContainer.scale.y;
                    defenderPosition.y += 30 * defenderCardContainer.scale.y;
                } else {
                    defenderPosition.y -= CARD_HEIGHT * defenderCardContainer.scale.y;
                    attackerPosition.y += 30 * attackerCardContainer.scale.y;
                }

                const attackVector = defenderPosition.subtract(attackerPosition);
                attackArrow.position.copyFrom(this.scene.toLocal(attackerPosition));
                attackArrow.width = attackVector.magnitude();
                attackArrow.rotation = Math.atan2(attackVector.y, attackVector.x);
                attackArrow.visible = true;

                const attackerPower = attackerCardContainer.cardInstance.currentPower;
                const defenderPower = defenderCardContainer.cardInstance.currentPower;

                if (attackerPower >= defenderPower && defenderCardContainer.cardInstance.type !== 'Leader') {
                    defenderCardContainer.showSkull();
                } else {
                    defenderCardContainer.hideSkull();
                }
            }
        }

        if (gameState.clientGameState.choosingAttackTargetMode) {
            const attackerPower = gameState.clientGameState.selectedCardInstance.currentPower;
            this.showSkulls(attackerPower, gameState.clientGameState.validAttackTargets);
        }

        if (!this.promptAdded) {
            this.promptAdded = true;

            const promptContainer = new PIXI.Container();
            promptContainer.label = 'promptContainer';
            promptContainer.visible = false;
            promptContainer.x = APP_WIDTH / 2;
            promptContainer.y = APP_HEIGHT / 2;
            this.scene.addChild(promptContainer);

            const promptBgTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.PROMPT_BG);
            const promptBg = new PIXI.Sprite(promptBgTexture);
            promptBg.label = 'promptBg';
            promptBg.anchor.set(0.5, 0.5);
            promptBg.scale.set(1.4);
            promptContainer.addChild(promptBg);

            const promptText = new PIXI.HTMLText({
                text: 'Prompt',
                style: {
                    fontFamily: 'Arial',
                    fontSize: 35,
                    fill: '#ffffff',
                },
            });
            promptText.label = 'promptText';
            promptText.anchor.set(0.5, 0.5);
            promptText.eventMode = 'none';
            promptContainer.addChild(promptText);
        } else {
            const promptContainer = this.scene.getChildByLabel('promptContainer');
            const promptText = promptContainer.getChildByLabel('promptText');
            promptContainer.visible = false;

            let shouldShowPrompt = !gameState.clientGameState.availableActions?.length;
            shouldShowPrompt &&= (gameState?.pendingAction?.currentStepData?.prompt || gameState.clientGameState.overridePrompt);

            if (gameState?.pendingAction?.expectedResponseType === 'targetSelection') {
                shouldShowPrompt &&= (gameState?.pendingAction?.targetingPlayerId === gameState.player.playerId);
            } else {
                shouldShowPrompt &&= (gameState?.pendingAction?.context?.triggeringPlayerId === gameState.player.playerId);
            }

            if (shouldShowPrompt) {
                promptText.text = gameState.clientGameState.overridePrompt || gameState.pendingAction.currentStepData.prompt;
                promptContainer.visible = true;
            }
        }

        if (!this.actionButtonsAdded) {
            this.actionButtonsAdded = true;

            const actionButtonsContainer = new ActionButtonsContainer();
            actionButtonsContainer.visible = false;
            actionButtonsContainer.label = 'actionButtonsContainer';
            actionButtonsContainer.x = APP_WIDTH / 2;
            actionButtonsContainer.y = APP_HEIGHT / 2;

            this.scene.addChild(actionButtonsContainer);
        } else {
            const actionButtonsContainer = this.scene.getChildByLabel('actionButtonsContainer');
            actionButtonsContainer.update(gameState);
            if (gameState.clientGameState.availableActions?.length) {
                actionButtonsContainer.visible = true;
            } else {
                actionButtonsContainer.visible = false;
            }
        }

        if (!this.playerAttachedEffectAdded) {
            this.playerAttachedEffectAdded = true;
            const circleEffectTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.CIRCLE_06);
            const circleEffect = new PIXI.Sprite(circleEffectTexture);
            circleEffect.label = 'circleEffect';
            circleEffect.visible = false;
            circleEffect.anchor.set(0.5);
            circleEffect.x = 0;
            circleEffect.y = 0;
            circleEffect.scale.set(0.5);
            // circleEffect.tint = '#fbe64e';
            this.scene.addChild(circleEffect);

            const glowingCardEffectTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.WHITE_CARD);
            const glowingCardEffect = new PIXI.Sprite(glowingCardEffectTexture);
            glowingCardEffect.label = 'glowingCardEffect';
            glowingCardEffect.visible = false;
            glowingCardEffect.anchor.set(0.5, 1);
            glowingCardEffect.x = 0;
            glowingCardEffect.y = 0;
            glowingCardEffect.width = CARD_WIDTH;
            glowingCardEffect.height = CARD_HEIGHT;
            // glowingCardEffect.scale.set(0.5);
            // glowingCardEffect.tint = '#fbe64e';
            this.scene.addChild(glowingCardEffect);
        }

        if (!this.donCardsContainerAdded) {
            this.donCardsContainerAdded = true;
            const donCardsContainer = new PIXI.Container();
            donCardsContainer.label = 'donCardsContainer';
            this.scene.addChild(donCardsContainer);
        }

        if (!this.drawCardAdded) {
            this.drawCardAdded = true;
            const drawCardContainer = new CardContainer();
            drawCardContainer.label = 'drawCardContainer';
            drawCardContainer.visible = false;
            drawCardContainer.x = APP_WIDTH / 2;
            drawCardContainer.y = APP_HEIGHT / 2;
            drawCardContainer.eventMode = 'none';
            this.scene.addChild(drawCardContainer);
        }

        if (!this.attackRenderLayerAdded) {
            this.attackRenderLayerAdded = true;
            const attackRenderLayer = new PIXI.RenderLayer();
            attackRenderLayer.label = 'attackRenderLayer';
            this.scene.addChild(attackRenderLayer);
        }

        if (!this.trashOverlayAdded) {
            this.trashOverlayAdded = true;
            const playerTrashOverlay = new TrashOverlayContainer(true);
            playerTrashOverlay.label = 'playerTrashOverlay';
            playerTrashOverlay.x = 832;
            playerTrashOverlay.y = 1940;
            playerTrashOverlay.visible = false;
            this.scene.addChild(playerTrashOverlay);

            const opponentTrashOverlay = new TrashOverlayContainer(false);
            opponentTrashOverlay.label = 'opponentTrashOverlay';
            opponentTrashOverlay.x = 252;
            opponentTrashOverlay.y = 571;
            opponentTrashOverlay.visible = false;
            this.scene.addChild(opponentTrashOverlay);

            playerTrashOverlay.eventMode = 'static';
            playerTrashOverlay.on('pointerdown', (event) => {
                event.stopPropagation();
            });

            opponentTrashOverlay.eventMode = 'static';
            opponentTrashOverlay.on('pointerdown', (event) => {
                event.stopPropagation();
            });
        } else {
            const playerTrashOverlay = this.scene.getChildByLabel('playerTrashOverlay');
            playerTrashOverlay.update(gameState.player);
            const opponentTrashOverlay = this.scene.getChildByLabel('opponentTrashOverlay');
            opponentTrashOverlay.update(gameState.opponent);
        }

        if (!this.phaseChangeVisualsAdded) {
            this.phaseChangeVisualsAdded = true;

            const counterStepText = new PIXI.Text({
                text: 'COUNTER STEP',
                style: {
                    fontFamily: 'Arial',
                    fontSize: 115,
                    fontWeight: 'bold',
                    fill: '#ffffff',
                    stroke: { color: '#000000', width: 15 },
                }
            });
            counterStepText.label = 'counterStepText';
            counterStepText.anchor.set(0.5);
            counterStepText.visible = false;
            counterStepText.x = APP_WIDTH / 2;
            counterStepText.y = APP_HEIGHT / 2;
            this.scene.addChild(counterStepText);

            const blockerStepText = new PIXI.Text({
                text: 'BLOCKER STEP',
                style: {
                    fontFamily: 'Arial',
                    fontSize: 115,
                    fontWeight: 'bold',
                    fill: '#ffffff',
                    stroke: { color: '#000000', width: 15 },
                }
            });
            blockerStepText.label = 'blockerStepText';
            blockerStepText.anchor.set(0.5);
            blockerStepText.visible = false;
            blockerStepText.x = APP_WIDTH / 2;
            blockerStepText.y = APP_HEIGHT / 2;
            this.scene.addChild(blockerStepText);
        }

        if (!this.whosTurnVisualsAdded) {
            this.whosTurnVisualsAdded = true;

            const yourTurnTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.YOUR_TURN);
            const yourTurnSprite = new PIXI.Sprite(yourTurnTexture);
            yourTurnSprite.label = 'yourTurnSprite';
            yourTurnSprite.anchor.set(0.5);
            yourTurnSprite.visible = false;
            yourTurnSprite.x = APP_WIDTH / 2;
            yourTurnSprite.y = APP_HEIGHT / 2;
            this.scene.addChild(yourTurnSprite);

            const opponentsTurnTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.OPPONENTS_TURN);
            const opponentsTurnSprite = new PIXI.Sprite(opponentsTurnTexture);
            opponentsTurnSprite.label = 'opponentsTurnSprite';
            opponentsTurnSprite.anchor.set(0.5);
            opponentsTurnSprite.visible = false;
            opponentsTurnSprite.x = APP_WIDTH / 2;
            opponentsTurnSprite.y = APP_HEIGHT / 2;
            this.scene.addChild(opponentsTurnSprite);
        }

        if (!this.effectActivationVisualsAdded) {
            this.effectActivationVisualsAdded = true;

            const effectActivationOverlayGraphics = new PIXI.Graphics();
            effectActivationOverlayGraphics.label = 'effectActivationOverlayGraphics';
            effectActivationOverlayGraphics.x = APP_WIDTH / 2;
            effectActivationOverlayGraphics.y = APP_HEIGHT / 2;
            effectActivationOverlayGraphics.rect(-APP_WIDTH / 2, -APP_HEIGHT / 2, APP_WIDTH, APP_HEIGHT);
            effectActivationOverlayGraphics.fill({ color: '#000000' });
            effectActivationOverlayGraphics.alpha = 0;
            effectActivationOverlayGraphics.eventMode = 'none';
            this.scene.addChild(effectActivationOverlayGraphics);

            const effectActivationRenderLayer = new PIXI.RenderLayer();
            effectActivationRenderLayer.label = 'effectActivationRenderLayer';
            this.scene.addChild(effectActivationRenderLayer);
        }

        if (!this.settingsVisualsAdded) {
            this.settingsVisualsAdded = true;

            const settingsOverlayContainer = new SettingsOverlayContainer();
            settingsOverlayContainer.x = APP_WIDTH / 2;
            settingsOverlayContainer.y = APP_HEIGHT / 2;
            this.scene.addChild(settingsOverlayContainer);

            const settingsButton = new ButtonContainer({ bgAssetName: AssetsLoader.eDefaultAssetsAliases.SETTINGS_BUTTON, eventName: 'settingsButtonClicked' });
            settingsButton.label = 'settingsButton';
            settingsButton.visible = true;
            settingsButton.x = APP_WIDTH - settingsButton.width / 2;
            settingsButton.y = settingsButton.height / 2;
            this.scene.addChild(settingsButton);
        }

        if (!this.triggerVisualsAdded) {
            this.triggerVisualsAdded = true;
            
            const opponentTriggerCardBackSprite = new PIXI.Sprite(PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.MAIN_DECK_CARD_BACK));
            opponentTriggerCardBackSprite.label = 'opponentTriggerCardBackSprite';
            opponentTriggerCardBackSprite.visible = false;
            opponentTriggerCardBackSprite.x = 540;
            opponentTriggerCardBackSprite.y = 650;
            opponentTriggerCardBackSprite.anchor.set(0.5);
            opponentTriggerCardBackSprite.width = CARD_WIDTH * 1.5;
            opponentTriggerCardBackSprite.height = CARD_HEIGHT * 1.5;
            this.scene.addChild(opponentTriggerCardBackSprite);

            const playerTriggerContainer = new PlayerTriggerContainer();
            playerTriggerContainer.label = 'playerTriggerContainer';
            playerTriggerContainer.visible = false;
            playerTriggerContainer.x = APP_WIDTH / 2;
            playerTriggerContainer.y = APP_HEIGHT / 2;
            this.scene.addChild(playerTriggerContainer);
        } else {
            const opponentTriggerCardBackSprite = this.scene.getChildByLabel('opponentTriggerCardBackSprite');
            opponentTriggerCardBackSprite.visible = false;
            const playerTriggerContainer = this.scene.getChildByLabel('playerTriggerContainer');
            playerTriggerContainer.visible = false;

            if (gameState.pendingAction?.type === 'TRIGGER_STEP') {
                if (gameState.pendingAction.triggeringPlayerId === gameState.opponent.playerId) {
                    opponentTriggerCardBackSprite.visible = true;
                } else if (gameState.pendingAction.triggeringPlayerId === gameState.player.playerId) {
                    playerTriggerContainer.setCardInstance(gameState.player.hand[gameState.player.hand.length-1]);
                    playerTriggerContainer.visible = true;
                }
            }
        }

        if (!this.searchDeckVisualsAdded) {
            this.searchDeckVisualsAdded = true;
            const searchDeckContainer = new SearchDeckContainer();
            searchDeckContainer.label = 'searchDeckContainer';
            searchDeckContainer.visible = false;
            searchDeckContainer.x = APP_WIDTH / 2;
            searchDeckContainer.y = APP_HEIGHT / 2;
            this.scene.addChild(searchDeckContainer);

            const opponentSearchDeckContainer = new OpponentSearchDeckContainer();
            opponentSearchDeckContainer.label = 'opponentSearchDeckContainer';
            opponentSearchDeckContainer.visible = false;
            opponentSearchDeckContainer.x = APP_WIDTH / 2;
            opponentSearchDeckContainer.y = APP_HEIGHT / 2;
            this.scene.addChild(opponentSearchDeckContainer);
        } else {
            const searchDeckContainer = this.scene.getChildByLabel('searchDeckContainer');
            const opponentSearchDeckContainer = this.scene.getChildByLabel('opponentSearchDeckContainer');

            searchDeckContainer.visible = false;
            opponentSearchDeckContainer.visible = false;

            if (gameState.clientGameState.searchDeckMode) {
                searchDeckContainer.visible = true;
                searchDeckContainer.update(gameState);
            } else if (gameState.clientGameState.opponentSearchDeckMode) {
                opponentSearchDeckContainer.visible = true;
                opponentSearchDeckContainer.update(gameState);
            }
        }

        if (!this.chooseFromOpponentHandVisualsAdded) {
            this.chooseFromOpponentHandVisualsAdded = true;
            const chooseFromOpponentHandContainer = new ChooseFromOpponentHandContainer();
            chooseFromOpponentHandContainer.label = 'chooseFromOpponentHandContainer';
            chooseFromOpponentHandContainer.visible = false;
            chooseFromOpponentHandContainer.x = APP_WIDTH / 2;
            chooseFromOpponentHandContainer.y = APP_HEIGHT / 2;
            this.scene.addChild(chooseFromOpponentHandContainer);
        } else {
            const chooseFromOpponentHandContainer = this.scene.getChildByLabel('chooseFromOpponentHandContainer');
            chooseFromOpponentHandContainer.visible = false;
            if (gameState.clientGameState.choosingFromOpponentHandMode) {
                chooseFromOpponentHandContainer.visible = true;
                chooseFromOpponentHandContainer.update(gameState);
            }
        }

        if (!this.revealCardsVisualsAdded) {
            this.revealCardsVisualsAdded = true;
            const revealCardsContainer = new RevealCardsContainer();
            revealCardsContainer.label = 'revealCardsContainer';
            revealCardsContainer.visible = false;
            revealCardsContainer.x = APP_WIDTH / 2;
            revealCardsContainer.y = APP_HEIGHT / 2;
            this.scene.addChild(revealCardsContainer);
        } else {
            const revealCardsContainer = this.scene.getChildByLabel('revealCardsContainer');
            revealCardsContainer.visible = false;
            if (gameState.pendingAction?.type === 'EFFECT_STEP_RESOLUTION' && gameState.pendingAction?.expectedResponseType === 'revealCards') {
                revealCardsContainer.visible = true;
                revealCardsContainer.update(gameState);
            }
        }

        if (!this.selectFromTrashVisualsAdded) {
            this.selectFromTrashVisualsAdded = true;
            const selectFromTrashContainer = new SelectFromTrashContainer();
            selectFromTrashContainer.label = 'selectFromTrashContainer';
            selectFromTrashContainer.visible = false;
            selectFromTrashContainer.x = APP_WIDTH / 2;
            selectFromTrashContainer.y = APP_HEIGHT / 2;
            this.scene.addChild(selectFromTrashContainer);
        } else {
            const selectFromTrashContainer = this.scene.getChildByLabel('selectFromTrashContainer');
            selectFromTrashContainer.visible = false;
            if (gameState.clientGameState.selectingFromTrashMode) {
                selectFromTrashContainer.visible = true;
                selectFromTrashContainer.update(gameState);
            }
        }

        if (!this.mulliganVisualsAdded) {
            this.mulliganVisualsAdded = true;
            
            const mulliganContainer = new MulliganContainer(gameState.player.hand, gameState.isMyTurn);
            mulliganContainer.label = 'mulliganContainer';
            mulliganContainer.visible = isMulliganStep;
            mulliganContainer.x = APP_WIDTH / 2;
            mulliganContainer.y = APP_HEIGHT / 2;
            this.scene.addChild(mulliganContainer);
        } else {
            const mulliganContainer = this.scene.getChildByLabel('mulliganContainer');
            if (isMulliganStep) {
                mulliganContainer.visible = true;
                mulliganContainer.update(gameState);
            } else {
                mulliganContainer.visible = false;
            }

        }

        if (!this.cardInfoAdded) {
            this.cardInfoAdded = true;

            const cardInfoContainer = new CardInfoContainer();
            cardInfoContainer.label = 'cardInfoContainer';
            cardInfoContainer.x = APP_WIDTH / 2;
            cardInfoContainer.y = APP_HEIGHT / 2;
            cardInfoContainer.visible = false;
            cardInfoContainer.eventMode = 'passive';
            this.scene.addChild(cardInfoContainer);
        }

        if (!this.winLoseContainerAdded) {
            this.winLoseContainerAdded = true;
            const winLoseContainer = new WinLoseContainer(gameState);
            winLoseContainer.label = 'winLoseContainer';
            winLoseContainer.x = APP_WIDTH / 2;
            winLoseContainer.y = APP_HEIGHT / 2;
            winLoseContainer.visible = false;
            this.scene.addChild(winLoseContainer);
        } else {
            const winLoseContainer = this.scene.getChildByLabel('winLoseContainer');
            winLoseContainer.visible = false;
            if (gameState.clientGameState.endResult === 'victory') {
                winLoseContainer.showVictory();
                winLoseContainer.visible = true;
            } else if (gameState.clientGameState.endResult === 'defeat') {
                winLoseContainer.showDefeat();
                winLoseContainer.visible = true;
            }
        }

        if (!this.ftuePopupAdded) {
            this.ftuePopupAdded = true;
            const ftuePopupContainer = new FtuePopupContainer();
            ftuePopupContainer.label = 'ftuePopupContainer';
            ftuePopupContainer.x = APP_WIDTH / 2;
            ftuePopupContainer.y = APP_HEIGHT / 2;
            ftuePopupContainer.visible = false;
            this.scene.addChild(ftuePopupContainer);
        }

        if (!this.fadeInOutLayoutAdded) {
            this.fadeInOutLayoutAdded = true;
            const fadeInOutLayout = new PIXI.Graphics();
            fadeInOutLayout.label = 'fadeInOutLayout';
            fadeInOutLayout.x = 0;
            fadeInOutLayout.y = 0;
            fadeInOutLayout.rect(0, 0, APP_WIDTH, APP_HEIGHT);            
            fadeInOutLayout.eventMode = 'none';
            fadeInOutLayout.visible = true;
            fadeInOutLayout.fill({ color: '#000000' });
            this.scene.addChild(fadeInOutLayout);
        }

        if (!this.addedStageClickListener) {
            this.addedStageClickListener = true;

            this.scene.eventMode = 'static';
            this.applyBoardAnchor();
            this.scene.on('pointerdown', () => {
                getGlobalEventEmitter()?.emit('stageClicked');
            });
        }

        const fpsText = new PIXI.Text({
            text: `FPS: ${PIXI.Ticker.shared.FPS.toFixed(2)}`,
            style: {
                fontSize: 40,
                fontFamily: 'Arial',
                fill: '#ffffff',
            }
        });
        fpsText.label = 'fpsText';
        fpsText.x = 10;
        fpsText.y = 10;
        // this.scene.addChild(fpsText);

        // PIXI.Ticker.shared.add((ticker) => {
        //     fpsText.text = `FPS: ${PIXI.Ticker.shared.FPS.toFixed(2)}`;
        // });
    }

    showCardInfo(cardInstance) {
        const cardInfoContainer = this.scene.getChildByLabel('cardInfoContainer');
        cardInfoContainer.update(cardInstance);
        cardInfoContainer.visible = true;
    }

    hideCardInfo() {
        const cardInfoContainer = this.scene.getChildByLabel('cardInfoContainer');
        cardInfoContainer.visible = false;
    }

    // Coins are awarded after the game-over broadcast, so this paints onto the
    // already-visible overlay instead of waiting for the next scene update.
    showMatchRewards(reward) {
        this.scene.getChildByLabel('winLoseContainer')?.showReward(reward);
    }

    toggleSettingsOverlay() {
        this.scene.getChildByLabel('settingsOverlayContainer')?.toggle();
    }

    showButton(buttonLabel) {
        const endTurnButton = this.scene.getChildByLabel('endTurnButton');
        const cancelButton = this.scene.getChildByLabel('cancelButton');
        const enemyTurnButton = this.scene.getChildByLabel('enemyTurnButton');
        const passButton = this.scene.getChildByLabel('passButton');
        const enemyCounterButton = this.scene.getChildByLabel('enemyCounterButton');

        endTurnButton.visible = false;
        cancelButton.visible = false;
        enemyTurnButton.visible = false;
        passButton.visible = false;
        enemyCounterButton.visible = false;

        this.scene.getChildByLabel(buttonLabel).visible = true;
    }

    showPlayerTrashOverlay() {
        this.scene.getChildByLabel('playerTrashOverlay').visible = true;
    }

    hidePlayerTrashOverlay() {
        this.scene.getChildByLabel('playerTrashOverlay').visible = false;
    }

    togglePlayerTrashOverlay() {
        const playerTrashOverlay = this.scene.getChildByLabel('playerTrashOverlay');
        playerTrashOverlay.visible = !playerTrashOverlay.visible;
    }

    showOpponentTrashOverlay() {
        this.scene.getChildByLabel('opponentTrashOverlay').visible = true;
    }

    hideOpponentTrashOverlay() {
        this.scene.getChildByLabel('opponentTrashOverlay').visible = false;
    }

    toggleOpponentTrashOverlay() {
        const opponentTrashOverlay = this.scene.getChildByLabel('opponentTrashOverlay');
        opponentTrashOverlay.visible = !opponentTrashOverlay.visible;
    }

    unhighlightAllCards() {
        this.scene.getChildByLabel('playerLeaderContainer', true)?.unhighlightCard();
        this.scene.getChildByLabel('opponentLeaderContainer', true)?.unhighlightCard();
        this.scene.getChildByLabel('playerHandContainer', true)?.unhighlightAllCards();
        this.scene.getChildByLabel('playerCharacterAreaContainer', true)?.unhighlightAllCards();
        this.scene.getChildByLabel('opponentCharacterAreaContainer', true)?.unhighlightAllCards();
    }

    hideSkulls() {
        this.scene.getChildByLabel('playerCharacterAreaContainer', true)?.hideSkulls();
        this.scene.getChildByLabel('opponentCharacterAreaContainer', true)?.hideSkulls();
    }

    showSkulls(attackerPower, defenders) {
        this.scene.getChildByLabel('playerCharacterAreaContainer', true)?.showSkulls(attackerPower, defenders);
        this.scene.getChildByLabel('opponentCharacterAreaContainer', true)?.showSkulls(attackerPower, defenders);
    }

    findCardContainer(cardInstanceId) {
        if (!cardInstanceId) {
            return null;
        }

        const searchInContainer = (container) => {
            // Skip if not a container or no children
            if (!container.children || container.children.length === 0) {
                return null;
            }

            // Check each child
            for (const child of container.children) {
                // If this is a card container with matching ID, return it
                if (child?.cardInstance?.instanceId === cardInstanceId) {
                    return child;
                }

                // Recursively search in child containers
                const found = searchInContainer(child);
                if (found) {
                    return found;
                }
            }

            return null;
        };

        // Start search from the scene
        return searchInContainer(this.scene);
    }

    async playAnimations(animations) {
        for (const animation of animations) {
            if (animation.type === 'player_played_character') {
                await this.#handlePlayerPlayedCharacterAnimation(animation);
            } else if (animation.type === 'player_played_stage') {
                await this.#handlePlayerPlayedStageAnimation(animation);
            } else if (animation.type === 'player_attached_don') {
                await this.#handlePlayerAttachedDonAnimation(animation);
            } else if (animation.type === 'opponent_attached_don') {
                await this.#handleOpponentAttachedDonAnimation(animation);
            } else if (animation.type === 'counter_step_started') {
                await this.#handleCounterStepStartedAnimation();
            } else if (animation.type === 'blocker_step_started') {
                await this.#handleBlockerStepStartedAnimation();
            } else if (animation.type === 'attack_happened') {
                await this.#handleAttackHappenedAnimation(animation);
            } else if (animation.type === 'your_turn') {
                await this.#handleYourTurnAnimation();
            } else if (animation.type === 'opponents_turn') {
                await this.#handleOpponentsTurnAnimation();
            } else if (animation.type === 'turn_changed') {
                await this.#handleTurnChangedAnimation();
            } else if (animation.type === 'opponent_played_character') {
                await this.#handleOpponentPlayedCharacterAnimation(animation);
            } else if (animation.type === 'effect_step_started') {
                await this.#handleEffectStepStartedAnimation(animation);
            } else if (animation.type === 'my_refresh_phase') {
                await this.#handleMyRefreshPhaseAnimation(animation);
            } else if (animation.type === 'player_taking_life_card') {
                await this.#handlePlayerTakingLifeCardAnimation(animation);
            } else if (animation.type === 'power_changed') {
                await this.#handlePowerChangedAnimation(animation);
            } else if (animation.type === 'player_rested_dons') {
                await this.#handlePlayerRestedDonsAnimation(animation);
            } else if (animation.type === 'opponent_rested_dons') {
                await this.#handleOpponentRestedDonsAnimation(animation);
            } else if (animation.type === 'use_counter_character') {
                await this.#handleUseCounterCharacterAnimation(animation);
            } else if (animation.type === 'player_drew_cards') {
                await this.#handlePlayerDrewCardsAnimation(animation);
            } else if (animation.type === 'opponent_reveal_cards') {
                await this.#handleOpponentRevealCardsAnimation(animation);
            } else if (animation.type === 'play_animation') {
                await this.#handlePlayAnimationAnimation(animation);
            } else if (animation.type === 'opponent_returned_character_to_hand') {
                await this.#handleOpponentReturnedCharacterToHandAnimation(animation);
            }
        }
    }

    async #handlePlayerPlayedCharacterAnimation(animation) {
        await Promise.all([
            this.scene.getChildByLabel('playerCharacterAreaContainer', true)?.playPlayerPlayedCharacterAnimation(animation.numOfCharacters),
            this.scene.getChildByLabel('playerHandContainer', true)?.playPlayerPlayedCharacterAnimation(animation.instanceId, animation.numOfCharacters, animation.fieldIndex),
        ]);
    }

    async #handlePlayerAttachedDonAnimation(animation) {
        const cardContainer = this.findCardContainer(animation.targetCardInstanceId);
        if (!cardContainer) {
            return;
        }

        const cardPosition = cardContainer.getGlobalPosition();
        await this.scene.getChildByLabel('playerDonsContainer', true)?.attachedDonAnimation(cardPosition, animation.wasRested, false);
        await this.attachedDonAnimation(cardPosition, -15);
    }

    async #handleOpponentAttachedDonAnimation(animation) {
        const cardContainer = this.findCardContainer(animation.targetCardInstanceId);
        if (!cardContainer) {
            return;
        }

        const cardPosition = cardContainer.getGlobalPosition();
        await this.scene.getChildByLabel('opponentDonsContainer', true)?.attachedDonAnimation(cardPosition, animation.wasRested, true);
        await this.attachedDonAnimation(cardPosition, 15);
    }

    async attachedDonAnimation(cardPosition, angle = -15) {
        const localCardPosition = this.scene.toLocal(cardPosition);
        const circleEffect = this.scene.getChildByLabel('circleEffect');
        circleEffect.visible = true;
        circleEffect.alpha = 0;
        circleEffect.scale.set(0.25);
        circleEffect.x = localCardPosition.x;
        circleEffect.y = localCardPosition.y - (CARD_HEIGHT * 0.5);

        const glowingCardEffect = this.scene.getChildByLabel('glowingCardEffect');
        glowingCardEffect.visible = true;
        glowingCardEffect.alpha = 1;
        glowingCardEffect.x = localCardPosition.x;
        glowingCardEffect.y = localCardPosition.y;
        glowingCardEffect.angle = angle;

        await gsap.timeline().to(glowingCardEffect, {
            duration: 0.6,
            ease: 'power2.out',
            pixi: {
                alpha: 0,
                angle: 0,
            }
        }).to(circleEffect, {
            duration: 0.1,
            pixi: {
                alpha: 1,
            }
        }, '0').to(circleEffect, {
            duration: 0.5,
            ease: 'power2.out',
            pixi: {
                alpha: 0,
            }
        }, '>').to(circleEffect, {
            duration: 0.6,
            ease: 'power2.out',
            pixi: {
                scale: 1,
            }
        }, '0');

        circleEffect.visible = false;
        glowingCardEffect.visible = false;
    }

    async #handleStepTextAnimation(label) {
        const stepText = this.scene.getChildByLabel(label);
        stepText.visible = true;
        stepText.alpha = 0;
        stepText.scale.x = 6;
        stepText.skew.x = -1.355;

        const stepTextTimeline = gsap.timeline();
        stepTextTimeline.to(stepText, {
            duration: 0.4,
            pixi: {
                alpha: 1,
                scaleX: 1,
                skewX: 0,
            }
        }).to(stepText, {
            duration: 0.4,
            pixi: {
                alpha: 0,
            }
        }, "+=0.55");

        await stepTextTimeline;
        
        stepText.visible = false;
    }

    async #handleCounterStepStartedAnimation() {
        await this.#handleStepTextAnimation('counterStepText');
    }

    async #handleBlockerStepStartedAnimation() {
        await this.#handleStepTextAnimation('blockerStepText');
    }

    async #handleAttackHappenedAnimation(animation) {
        const attackRenderLayer = this.scene.getChildByLabel('attackRenderLayer');

        const attacker = this.findCardContainer(animation.attackerId);
        const defender = this.findCardContainer(animation.defenderId);

        const attackerPosition = attacker.getGlobalPosition();
        const defenderPosition = defender.getGlobalPosition();
        const localDefenderPosition = this.scene.toLocal(defenderPosition);

        let attackerVector = defenderPosition.subtract(attackerPosition);
        attackerVector = attackerVector.multiplyScalar(0.85);
        const windupVector = new PIXI.Point().copyFrom(attackerVector).normalize().multiplyScalar(-1);

        const attackDonCountContainer = attacker.getChildByLabel('donCountContainer');
        const attackerPowerTextContainer = attacker.getChildByLabel('powerTextContainer');

        const counterBubbleTexture = PIXI.Assets.get(AssetsLoader.eDefaultAssetsAliases.COUNTER_BUBBLE);
        const defenderCounterBubble = new PIXI.Sprite(counterBubbleTexture);
        defenderCounterBubble.label = 'counterBubble';
        defenderCounterBubble.visible = false;
        defenderCounterBubble.anchor.set(0.5);
        defenderCounterBubble.position.copyFrom(localDefenderPosition);
        defenderCounterBubble.y -= (CARD_HEIGHT / 2) * defender.scale.y;
        this.scene.addChild(defenderCounterBubble);

        CustomWiggle.create('gettingHurtX', {
            wiggles: 10,
            type: 'random',
        });
        CustomWiggle.create('gettingHurtY', {
            wiggles: 10,
            type: 'random',
        });

        attackRenderLayer.attach(attacker);

        const attackerOriginalPosition = attacker.position.clone();
        const attackerOriginalScale = attacker.scale.clone();

        await gsap.timeline().to(attacker, {
            duration: 0.5,
            ease: 'none',
            pixi: {
                scale: 1.25,
            }
        }).to(attackDonCountContainer, {
            duration: 0.1,
            ease: 'none',
            pixi: {
                scale: 0,
            }
        }, '<').to(attackerPowerTextContainer, {
            duration: 0.1,
            ease: 'none',
            pixi: {
                scale: 0,
                y: '-=50',
            }
        }, '<').to(attacker, {
            duration: 0.3,
            ease: 'power1.out',
            pixi: { 
                x: attackerOriginalPosition.x + windupVector.x * 200,
                y: attackerOriginalPosition.y + windupVector.y * 200,
            }
        }, `-=${0.3}`).to(attacker, {
            duration: 0.18,
            ease: 'none',
            pixi: {
                x: attackerOriginalPosition.x + attackerVector.x,
                y: attackerOriginalPosition.y + attackerVector.y,
                scaleX: attackerOriginalScale.x,
                scaleY: attackerOriginalScale.y,
            }
        });

        let showHeartDamage = false;
        if (!animation.successfulAttack) {
            defenderCounterBubble.visible = true;
            defenderCounterBubble.rotation = Math.atan2(attackerVector.y, attackerVector.x);
            defenderCounterBubble.angle -= 90;
            AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.COUNTER_BUBBLE_SOUND)?.play();
        } else {
            AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.HIT_SOUND)?.play();
            showHeartDamage = defender.cardInstance.type === 'Leader';
        }

        if (showHeartDamage) {
            const heartDamageAnimatedSprite = defender.getChildByLabel('heartDamageAnimatedSprite', true);
            heartDamageAnimatedSprite.visible = true;
            heartDamageAnimatedSprite.scale.set(0.3);
            heartDamageAnimatedSprite.alpha = 1;
            heartDamageAnimatedSprite.gotoAndPlay(0);

            gsap.timeline().to(heartDamageAnimatedSprite, {
                duration: 1,
                ease: 'elastic.out(2, 0.3)',
                pixi: {
                    scale: 1,
                }
            }).to(heartDamageAnimatedSprite, {
                duration: 1,
                ease: 'power2.in',
                pixi: {
                    alpha: 0,
                }
            });
        }

        const shakePower = 15;
        await Promise.all([
            gsap.to(this.scene, {
                duration: 0.3,
                ease: 'gettingHurtX',
                pixi: { x: shakePower }
            }),
            gsap.to(this.scene, {
                duration: 0.3,
                ease: 'gettingHurtY',
                pixi: { y: shakePower }
            }),
            gsap.timeline().to(attacker, {
                duration: 1,
                ease: 'power3.inOut',
                pixi: {
                    x: attackerOriginalPosition.x,
                    y: attackerOriginalPosition.y,
                }
            }).to(attackDonCountContainer, {
                duration: 0.2,
                ease: 'none',
                pixi: {
                    scale: 1,
                }
            }, '<+=0.5').to(attackerPowerTextContainer, {
                duration: 0.2,
                ease: 'none',
                pixi: {
                    scale: 1,
                    y: '+=50',
                }
            }, '<').to(defenderCounterBubble, {
                duration: 1,
                ease: 'power2.out',
                pixi: {
                    alpha: 0,
                    scale: 1.3,
                }
            }, '0'),
        ]);

        defenderCounterBubble.destroy();
        attackRenderLayer.detach(attacker);
    }

    async #handleYourTurnAnimation() {
        const yourTurnSprite = this.scene.getChildByLabel('yourTurnSprite');
        yourTurnSprite.visible = true;
        yourTurnSprite.scale.set(0.3);

        this.showButton('endTurnButton');

        AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.SHIP_BELL_WITH_SEAGULLS_SOUND)?.play();

        // TODO: add waves switch animation

        await gsap.timeline().to(yourTurnSprite, {
            duration: 0.5,
            ease: 'back.out(2)',
            pixi: {
                scale: 1,
            }
        }).call(() => {}, [], '+=0.5');
        
        gsap.timeline().to(yourTurnSprite, {
            duration: 0.3,
            ease: 'power2.in',
            pixi: {
                scale: 0.3,
            }
        }, '0.8').call(() => {
            yourTurnSprite.visible = false;
        });
    }

    async #handleOpponentsTurnAnimation() {
        const opponentsTurnSprite = this.scene.getChildByLabel('opponentsTurnSprite');
        opponentsTurnSprite.visible = true;
        opponentsTurnSprite.scale.set(0.3);

        this.showButton('enemyTurnButton');

        AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.CROWS_SOUND)?.play();

        // TODO: add waves switch animation

        await gsap.timeline().to(opponentsTurnSprite, {
            duration: 0.5,
            ease: 'back.out(2)',
            pixi: {
                scale: 1,
            }
        }).call(() => {}, [], '+=0.5');
        
        gsap.timeline().to(opponentsTurnSprite, {
            duration: 0.3,
            ease: 'power2.in',
            pixi: {
                scale: 0.3,
            }
        }, '0.8').then(() => {
            opponentsTurnSprite.visible = false;
        });
    }

    async #handleTurnChangedAnimation() {
        this.wavesTimer = 0;
    }

    async #handleOpponentPlayedCharacterAnimation(animation) {
        await Promise.all([
            this.scene.getChildByLabel('opponentCharacterAreaContainer', true)?.playPlayerPlayedCharacterAnimation(animation.numOfCharacters),
            this.scene.getChildByLabel('opponentHandContainer', true)?.playOpponentPlayedCharacterAnimation(animation.cardInstance, animation.numOfCharacters, animation.fieldIndex),
        ]);
    }

    async #handleEffectStepStartedAnimation(animation) {
        const cardContainer = this.findCardContainer(animation.cardInstance.instanceId);
        const cardVisualsContainer = cardContainer.getChildByLabel('cardVisualsContainer');
        const effectActivationOverlayGraphics = this.scene.getChildByLabel('effectActivationOverlayGraphics');
        const effectActivationRenderLayer = this.scene.getChildByLabel('effectActivationRenderLayer');
        const effectActivationSprite = cardVisualsContainer.getChildByLabel('effectActivationSprite');
        const cardGlow = cardVisualsContainer.getChildByLabel('cardGlow');

        effectActivationRenderLayer.attach(cardContainer);

        const originalScale = cardContainer.scale;
        cardContainer.pivot.set(0, -CARD_HEIGHT / 2);
        cardContainer.y -= CARD_HEIGHT / 2 * originalScale.y;
        cardContainer.animationProgress = 0;

        effectActivationSprite.visible = true;
        effectActivationSprite.alpha = 1;
        cardVisualsContainer.setHighlightParameters(0, 0, '#0084ff');
        cardGlow.visible = true;
        cardGlow.alpha = 0;

        CardVisualsContainer.highlightFilterSpeed = 0.002;

        function onUpdate() {
            cardVisualsContainer.setHighlightParameters(1 * cardContainer.animationProgress, 5 * cardContainer.animationProgress, '#0084ff');
        }

        AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.EFFECT_ACTIVATION_SOUND)?.play();

        await gsap.timeline({ onUpdate }).timeScale(1).to(cardContainer, {
            duration: 0.5,
            ease: 'back.out(2)',
            animationProgress: 1,
            pixi: {
                scaleX: originalScale.x * 1.5,
                scaleY: originalScale.y * 1.5,
            },
        }).to(effectActivationOverlayGraphics, {
            duration: 0.3,
            ease: 'power1.inOut',
            alpha: 0.55,
        }, '<').to(cardGlow, {
            duration: 0.3,
            ease: 'power1.inOut',
            alpha: 1,
        }, '<').to(cardContainer, {
            duration: 0.3,
            ease: 'power1.inOut',
            animationProgress: 0,
            pixi: {
                scaleX: originalScale.x,
                scaleY: originalScale.y,
            },
        }, '+=0.3').to(effectActivationOverlayGraphics, {
            duration: 0.3,
            ease: 'power1.inOut',
            alpha: 0,
        }, '<').to(cardGlow, {
            duration: 0.3,
            ease: 'power1.inOut',
            alpha: 0,
        }, '<');

        effectActivationSprite.visible = false;

        cardContainer.pivot.set(0, 0);
        cardContainer.y += CARD_HEIGHT / 2 * originalScale.y;

        effectActivationRenderLayer.detach(cardContainer);

        CardVisualsContainer.highlightFilterSpeed = 0.0004;
    }

    disableClicks() {
        this.originalEventMode = this.scene.eventMode;
        this.scene.eventMode = 'none';
    }

    enableClicks() {
        this.scene.eventMode = this.originalEventMode;
    }

    async showFtuePopup() {
        const ftuePopupContainer = this.scene.getChildByLabel('ftuePopupContainer');
        ftuePopupContainer.alpha = 0;
        ftuePopupContainer.scale.set(0.3);
        ftuePopupContainer.visible = true;
        ftuePopupContainer.eventMode = 'none';

        await gsap.timeline().to(ftuePopupContainer, {
            duration: 0.5,
            ease: 'power2.out',
            pixi: {
                alpha: 1,
                scale: 1,
            }
        });

        ftuePopupContainer.eventMode = 'static';
    }

    hideFtuePopup() {
        const ftuePopupContainer = this.scene.getChildByLabel('ftuePopupContainer');
        ftuePopupContainer.visible = false;
    }

    async fadeIn() {
        const fadeInOutLayout = this.scene.getChildByLabel('fadeInOutLayout');
        fadeInOutLayout.visible = true;
        fadeInOutLayout.alpha = 1;

        await gsap.timeline().to(fadeInOutLayout, {
            duration: 1,
            // ease: 'power2.out',
            pixi: {
                alpha: 0,
            }
        });

        fadeInOutLayout.visible = false;
    }

    async #handlePlayerPlayedStageAnimation(animation) {
        await this.scene.getChildByLabel('playerHandContainer', true)?.playPlayerPlayedStageAnimation(animation.instanceId);
    }

    async #handleMyRefreshPhaseAnimation(animation) {
        const donsOnField = [];
        const restedDons = [];
        const donCardsContainer = this.scene.getChildByLabel('donCardsContainer');

        animation.newGameState.player.active_don.forEach(donCard => {
            let cardAttachedTo;
            animation.prevGameState.player.leader.attachedDon.forEach(card => {
                if (card.instanceId === donCard.instanceId) {
                    cardAttachedTo = animation.prevGameState.player.leader.instanceId;
                }
            });
            if (!cardAttachedTo) {
                animation.prevGameState.player.field_characters.forEach(character => {
                    if (character.attachedDon.find(don => don.instanceId === donCard.instanceId)) {
                        cardAttachedTo = character.instanceId;
                    }
                });
            }

            if (cardAttachedTo) {
                console.log('card found in', cardAttachedTo);
                donsOnField.push({ donCard, cardAttachedTo });
            } else {
                animation.prevGameState.player.active_don.forEach(prevStateDon => {
                    if (prevStateDon.instanceId === donCard.instanceId) {
                        if (prevStateDon.isRested) {
                            restedDons.push(prevStateDon);
                        }
                    }
                });
            }
        });

        const donCardsTimeline = gsap.timeline();
        const playerField = this.scene.getChildByLabel(PLAYER_FIELD_LABEL);
        const playerDonsContainer = playerField.getChildByLabel('playerDonsContainer');

        if (donsOnField.length > 0) {
            const donsCountPerCardAttachedTo = {};
            for (let i=0; i<donsOnField.length; i++) {
                const { donCard, cardAttachedTo } = donsOnField[i];
                const cardAttachedToContainer = this.findCardContainer(cardAttachedTo);
                const cardAttachedToInstance = cardAttachedToContainer.cardInstance;
                const totalCardsAttached = cardAttachedToInstance.attachedDon.length;
                donsCountPerCardAttachedTo[cardAttachedTo] = (donsCountPerCardAttachedTo[cardAttachedTo] || 0) + 1;
                const attachementOrder = donsCountPerCardAttachedTo[cardAttachedTo];
                
                const donCardContainer = new CardContainer({ type: 'DON!!' });
                donCardContainer.label = 'donCardContainer';
                donCardContainer.showFrontFace();
                donCardContainer.alpha = 0;
                donCardContainer.scale.copyFrom(cardAttachedToContainer.scale);
                donCardContainer.position.copyFrom(cardAttachedToContainer.getGlobalPosition());
                donCardsContainer.addChild(donCardContainer);

                donCardsTimeline.to(donCardContainer, {
                    duration: 0.5,
                    ease: 'power1.out',
                    pixi: {
                        x: 250,
                        y: 1935,
                        scale: 0.56,
                    }
                }, `${i*0.15}`).call(() => {
                    cardAttachedToContainer.setAttachedDonsCounter(totalCardsAttached - attachementOrder);
                }, [], '<').to(donCardContainer, {
                    duration: 0.2,
                    ease: 'power2.out',
                    yoyo: true,
                    repeat: 1,
                    pixi: {
                        alpha: 1,
                    }
                }, '<').call(() => {
                    playerDonsContainer.incrementActiveDonsCounter(1);
                }, [], '>-=0.07');
            }
        }

        if (restedDons.length > 0) {
            for (let i=0; i<restedDons.length; i++) {
                const donCard = restedDons[i];
                const donCardContainer = new CardContainer({ type: 'DON!!' });
                donCardContainer.label = 'donCardContainer';
                donCardContainer.x = 250;
                donCardContainer.y = 2035;
                donCardContainer.scale.set(0.56);
                donCardContainer.angle = 90;
                donCardContainer.pivot.set(0, -CARD_HEIGHT / 2);
                donCardContainer.alpha = 0;
                donCardContainer.showFrontFace();
                donCardsContainer.addChild(donCardContainer);

                donCardsTimeline.to(donCardContainer, {
                    duration: 0.5,
                    ease: 'power1.out',
                    pixi: {
                        x: 250,
                        y: 1935 - (CARD_HEIGHT / 2) * donCardContainer.scale.y,
                        angle: 0,
                    }
                }, `${(i+donsOnField.length)*0.15}`).call(() => {
                    playerDonsContainer.decreaseRestedDonsCounter(1);
                }, [], '<').to(donCardContainer, {
                    duration: 0.2,
                    ease: 'power2.out',
                    yoyo: true,
                    repeat: 1,
                    pixi: {
                        alpha: 1,
                    }
                }, '<').call(() => {
                    playerDonsContainer.incrementActiveDonsCounter(1);
                }, [], '>-=0.07');
            }
        }

        await donCardsTimeline;
        while (donCardsContainer.children.length > 0) {
            donCardsContainer.children[0].destroy();
        }
    }

    async #handlePlayerTakingLifeCardAnimation(animation) {
        const playerTriggerContainer = this.scene.getChildByLabel('playerTriggerContainer');
        playerTriggerContainer.setCardInstance(animation.lifeCard);

        const playerTriggerBackground = playerTriggerContainer.getChildByLabel('playerTriggerBackground');
        const playerTriggerCardContainer = playerTriggerContainer.getChildByLabel('playerTriggerCardContainer');
        const title = playerTriggerContainer.getChildByLabel('title');
        const actionButtonsContainer = playerTriggerContainer.getChildByLabel('actionButtonsContainer');

        playerTriggerBackground.alpha = 0;
        playerTriggerCardContainer.alpha = 0;
        title.alpha = 0;
        actionButtonsContainer.alpha = 0;

        const playerLeaderContainer = this.scene.getChildByLabel('playerLeaderContainer', true);
        const lifeCardFromLeader = await playerLeaderContainer.playDrawTopLifeCardAnimation();

        const newLifeCard = new CardContainer(animation.lifeCard);
        newLifeCard.hidePowerText();
        newLifeCard.scale.set(0, 0.55);
        newLifeCard.pivot.set(0, -CARD_HEIGHT / 2);
        newLifeCard.position.copyFrom(playerTriggerContainer.toLocal(lifeCardFromLeader.getGlobalPosition()));
        playerTriggerContainer.addChild(newLifeCard);

        playerTriggerContainer.visible = true;

        await gsap.timeline().timeScale(1.2).to(newLifeCard, {
            duration: 0.5,
            ease: 'power2.out',
            pixi: {
                scaleX: 0.55,
            }
        });

        const cardPositionInTriggerContainer = playerTriggerContainer.toLocal(playerTriggerCardContainer.getGlobalPosition());
        await gsap.timeline().timeScale(1.5).to(playerTriggerBackground, {
            duration: 0.5,
            ease: 'power2.out',
            pixi: {
                alpha: 0.7,
            }
        }).to(newLifeCard, {
            duration: 1,
            ease: 'power2.inOut',
            pixi: {
                x: cardPositionInTriggerContainer.x,
                y: cardPositionInTriggerContainer.y,
                scaleX: playerTriggerCardContainer.scale.x,
                scaleY: playerTriggerCardContainer.scale.y,
                pivotY: 0,
            }
        }, '<').to(title, {
            duration: 0.5,
            ease: 'power2.out',
            pixi: {
                alpha: 1,
            }
        }, '<+=0.2').to(actionButtonsContainer, {
            duration: 0.5,
            ease: 'power2.out',
            pixi: {
                alpha: 1,
            }
        }, '<+=0.2');

        playerTriggerCardContainer.alpha = 1;
        newLifeCard.destroy();
    }

    async #handlePowerChangedAnimation(animation) {
        const promiseList = [];
        animation.cardsWithChangedPower.forEach(card => {
            const cardContainer = this.findCardContainer(card.instanceId);
            if (!cardContainer) {
                return;
            }
            promiseList.push(cardContainer.playPowerChangedAnimation(card.prevPower, card.currentPower));
        });

        await Promise.all(promiseList);
    }

    async #handlePlayerRestedDonsAnimation(animation) {
        const { donsRestedCount } = animation;
        const playerDonsContainer = this.scene.getChildByLabel('playerDonsContainer', true);
        const donCardsContainer = playerDonsContainer.getChildByLabel('donCardsContainerForAnimation');

        const donCardsTimeline = gsap.timeline();

        const startPosition = donCardsContainer.toLocal({ x: 250, y: 1935 - (CARD_HEIGHT / 2) * 0.56 }, this.scene);
        const endPosition = donCardsContainer.toLocal({ x: 250, y: 2035 }, this.scene);
        for (let i=0; i<donsRestedCount; i++) {
            const donCardContainer = new CardContainer({ type: 'DON!!' });
            donCardContainer.label = 'donCardContainer';
            donCardContainer.scale.set(0.56);
            donCardContainer.x = startPosition.x;
            donCardContainer.y = startPosition.y;
            donCardContainer.angle = 0;
            donCardContainer.pivot.set(0, -CARD_HEIGHT / 2);
            donCardContainer.alpha = 0;
            donCardContainer.showFrontFace();
            donCardsContainer.addChild(donCardContainer);

            donCardsTimeline.to(donCardContainer, {
                duration: 0.5,
                ease: 'power1.out',
                pixi: {
                    x: endPosition.x,
                    y: endPosition.y,
                    angle: 90,
                }
            }, `${i*0.15}`).call(() => {
                playerDonsContainer.decreaseActiveDonsCounter(1);
            }, [], '<').to(donCardContainer, {
                duration: 0.2,
                ease: 'power2.out',
                yoyo: true,
                repeat: 1,
                pixi: {
                    alpha: 1,
                }
            }, '<').call(() => {
                playerDonsContainer.incrementRestedDonsCounter(1);
            }, [], '>-=0.07');
        }

        await donCardsTimeline;
        while (donCardsContainer.children.length > 0) {
            donCardsContainer.children[0].destroy();
        }
    }

    async #handleOpponentRestedDonsAnimation(animation) {
        const { opponentDonsRestedCount } = animation;
        const donCardsContainer = this.scene.getChildByLabel('donCardsContainer');
        const opponentDonsContainer = this.scene.getChildByLabel('opponentDonsContainer', true);

        const donCardsTimeline = gsap.timeline();

        for (let i=0; i<opponentDonsRestedCount; i++) {
            const donCardContainer = new CardContainer({ type: 'DON!!' });
            donCardContainer.label = 'donCardContainer';
            donCardContainer.scale.set(0.56);
            donCardContainer.x = 835;
            donCardContainer.y = 475;
            donCardContainer.angle = 0;
            donCardContainer.pivot.set(0, -CARD_HEIGHT / 2);
            donCardContainer.alpha = 0;
            donCardContainer.showFrontFace();
            donCardsContainer.addChild(donCardContainer);

            donCardsTimeline.to(donCardContainer, {
                duration: 0.5,
                ease: 'power1.out',
                pixi: {
                    x: 835,
                    y: 295,
                    angle: 90,
                }
            }, `${i*0.15}`).call(() => {
                opponentDonsContainer.decreaseActiveDonsCounter(1);
            }, [], '<').to(donCardContainer, {
                duration: 0.2,
                ease: 'power2.out',
                yoyo: true,
                repeat: 1,
                pixi: {
                    alpha: 1,
                }
            }, '<').call(() => {
                opponentDonsContainer.incrementRestedDonsCounter(1);
            }, [], '>-=0.07');
        }

        await donCardsTimeline;
        // while (donCardsContainer.children.length > 0) {
        //     donCardsContainer.children[0].destroy();
        // }
    }

    async #handleUseCounterCharacterAnimation(animation) {
        console.log('use counter character animation', animation);
        const { cardInstance } = animation;
        
        const cardContainer = this.findCardContainer(cardInstance.instanceId);
        cardContainer.zIndex = 100;
        cardContainer.y = -25;
        cardContainer.rotation = 0;

        const glowingCardEffect = cardContainer.getChildByLabel('glowingCardEffect', true);
        glowingCardEffect.visible = true;
        glowingCardEffect.tint = '#ffff00';
        glowingCardEffect.alpha = 0;

        const timeline = gsap.timeline().timeScale(0.8);

        AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.COUNTER_SOUND)?.play();

        await timeline.to(glowingCardEffect, {
            duration: 0.1,
            ease: 'power1.out',
            pixi: {
                alpha: 1,
            }
        }).to(cardContainer, {
            duration: 0.1,
            ease: 'power1.out',
            pixi: {
                y: '-=100',
                scaleX: 0,
                scaleY: 1.5,
            }
        }, '<+=0.05');
    }

    async #drawCardAnimation(cardInstance) {
        const drawCardContainer = this.scene.getChildByLabel('drawCardContainer');
        const cardVisualsContainer = drawCardContainer.getChildByLabel('cardVisualsContainer');
        drawCardContainer.updateCardInstance(cardInstance);
        drawCardContainer.hidePowerText();
        drawCardContainer.pivot.set(0, -CARD_HEIGHT / 2);
        drawCardContainer.angle = -90;
        cardVisualsContainer.scale.y = 0;
        drawCardContainer.scale.set(1);
        drawCardContainer.x = 990;
        drawCardContainer.y = 1885;
        drawCardContainer.visible = true;

        AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.CARD_DRAW_SOUND)?.play();

        await gsap.timeline().timeScale(2).to(cardVisualsContainer, {
            duration: 2.5,
            ease: 'power2.out',
            pixi: {
                scaleY: 1,
            }
        }).to(drawCardContainer, {
            duration: 2,
            ease: 'power3.out',
            pixi: {
                x: APP_WIDTH / 2,
            }
        }, '<').to(drawCardContainer, {
            duration: 2.5,
            ease: 'power1.out',
            pixi: {
                angle: 0,
            }
        }, '<').to(drawCardContainer, {
            duration: 2.5,
            ease: 'power1.inOut',
            pixi: {
                y: 1500,
            }
        }, '<').to(drawCardContainer, {
            duration: 2.5,
            ease: 'power2.out',
            pixi: {
                scale: 1.7,
            }
        }, '<').call(() => {}, [], '+=1');

        drawCardContainer.visible = false;

        const playerHandContainer = this.scene.getChildByLabel('playerHandContainer', true);
        await playerHandContainer.playPlayerAddCardToHandAnimation(cardInstance);
    }

    async #handlePlayerDrewCardsAnimation(animation) {
        for (const cardInstance of animation.cardsDrawn) {
            await this.#drawCardAnimation(cardInstance);
        }
    }

    async #handleOpponentRevealCardsAnimation(animation) {
        await this.scene.getChildByLabel('opponentSearchDeckContainer', true)?.playOpponentRevealCardsAnimation(animation.cards);
    }

    async #handlePlayAnimationAnimation(animation) {
        await animationsManager.playAnimation(this.scene, animation);
    }

    async #handleOpponentReturnedCharacterToHandAnimation(animation) {
        const { cardInstanceId, newHand } = animation;
        const cardContainer = this.findCardContainer(cardInstanceId);
        if (!cardContainer) {
            return;
        }
        
        cardContainer.hidePowerText();
        cardContainer.getChildByLabel('donCountContainer').visible = false;
        cardContainer.y -= (CARD_HEIGHT / 2) * cardContainer.scale.y;
        cardContainer.pivot.set(0, -CARD_HEIGHT / 2);

        const cardVisualsContainer = cardContainer.getChildByLabel('cardVisualsContainer');

        const opponentHandContainer = this.scene.getChildByLabel('opponentHandContainer', true);
        const targetGlobalPosition = opponentHandContainer?.toGlobal(opponentHandContainer?.calculateCardPosition(newHand.length, newHand.length-1));
        const newCardPosition = cardContainer.parent.toLocal(targetGlobalPosition);
        const targetRotation = opponentHandContainer?.calculateCardRotation(newHand.length, newHand.length-1);

        const moveCardsInHandTimeline = opponentHandContainer.playMoveHandCardsOnePosition();

        const backCardContainer = new CardContainer({});
        backCardContainer.pivot.set(0, -CARD_HEIGHT / 2);
        backCardContainer.position = opponentHandContainer.toLocal(cardContainer.getGlobalPosition());
        opponentHandContainer.addChild(backCardContainer);

        const backCardVisualsContainer = backCardContainer.getChildByLabel('cardVisualsContainer');
        backCardVisualsContainer.scale.set(0, 1);
        backCardVisualsContainer.angle = -90;

        const backCardTargetPosition = backCardContainer.parent.toLocal(targetGlobalPosition);

        const timeline = gsap.timeline();
        timeline.to(cardContainer, {
            duration: 0.5,
            ease: 'power2.out',
            pixi: {
                scale: 1.3,
            }
        }).to(cardContainer, {
            duration: 0.4,
            ease: 'power2.in',
            pixi: {
                x: newCardPosition.x,
                y: newCardPosition.y,
                scale: 1,
                pivotY: 0,
            }
        }, '+=0.2').to(cardVisualsContainer, {
            duration: 0.4,
            ease: 'power2.in',
            pixi: {
                scaleX: 0,
                angle: 90,
            }
        }, '0.5');

        const backCardTimeline = gsap.timeline();
        backCardTimeline.to(backCardContainer, {
            duration: 0.5,
            ease: 'power2.out',
            pixi: {
                scale: 1.3,
            }
        }).to(backCardContainer, {
            duration: 0.4,
            ease: 'power2.in',
            pixi: {
                x: backCardTargetPosition.x,
                y: backCardTargetPosition.y,
                scale: 1,
                pivotY: 0,
            }
        }, '+=0.2').to(backCardVisualsContainer, {
            duration: 0.4,
            ease: 'power2.out',
            pixi: {
                scaleX: 1,
                angle: 0,
            }
        }, '0.9').to(backCardContainer, {
            duration: 0.4,
            ease: 'power2.out',
            pixi: {
                angle: targetRotation * (180 / Math.PI),
            }
        }, '<');

        await Promise.all([moveCardsInHandTimeline, timeline, backCardTimeline]);
    }
}
